import { useState, useEffect, useCallback, useMemo } from "react";
import useUser from "~/contexts/user";
import { useV1List } from "~/hooks/useV1List";
import { ACCESS_CONTROLLER_V1 } from "~/utils/accessController";

const JOB_POLL_INTERVAL_MS = 1_500;
const AUTO_DISMISS_MS = 60_000;

export type BulkJobType = "create" | "update";

export interface BulkJobError {
  email?: string;
  tag?: string;
  error: string;
}

export interface BulkJob {
  jobId: string;
  totalUsers: number;
  processedUsers: number;
  status: "pending" | "in_progress" | "completed" | "failed";
  errors: BulkJobError[];
  startedAt: Date;
  completedAt?: Date;
  jobType?: BulkJobType;
}

interface BulkJobsListItem {
  job_id: string;
  status: "pending" | "in_progress" | "completed";
  total_users: number;
  processed_users: number;
  errors: { email?: string; tag?: string; error: string }[];
  created_at: string;
  completed_at?: string;
  created_by?: string;
  job_type?: BulkJobType;
}

interface TrackedJob {
  jobId: string;
  jobType: BulkJobType;
  startedAt: Date;
  initialTotalUsers: number;
  firedComplete: boolean;
  dismissedAt?: number;
}

const JOBS_URL = `${ACCESS_CONTROLLER_V1}/users/bulk-jobs`;

export function useBulkJobs({ onJobComplete }: { onJobComplete: () => void }) {
  const user = useUser();
  const [trackedJobs, setTrackedJobs] = useState<Map<string, TrackedJob>>(new Map());
  const [currentIndex, setCurrentIndex] = useState(0);

  const hasActiveJobs = [...trackedJobs.values()].some((j) => !j.dismissedAt);

  const { data: jobsData } = useV1List<BulkJobsListItem>(JOBS_URL, {
    refreshInterval: hasActiveJobs ? JOB_POLL_INTERVAL_MS : 0,
  });

  // Hydrate tracked jobs from server on load — picks up any in-progress jobs after a page reload.
  useEffect(() => {
    if (!jobsData || !user?.email) {
      return;
    }
    const userEmail = user.email;
    const newEntries = jobsData
      .filter(
        (j) => (j.status === "pending" || j.status === "in_progress") && j.created_by === userEmail
      )
      .map((j): [string, TrackedJob] => [
        j.job_id,
        {
          jobId: j.job_id,
          jobType: j.job_type ?? "update",
          startedAt: new Date(j.created_at),
          initialTotalUsers: j.total_users,
          firedComplete: false,
        },
      ]);
    if (newEntries.length === 0) {
      return;
    }
    setTrackedJobs((prev) => {
      const toAdd = newEntries.filter(([id]) => !prev.has(id));
      if (toAdd.length === 0) {
        return prev;
      }
      return new Map([...prev, ...toAdd]);
    });
  }, [jobsData, user?.email]);

  // Fire onJobComplete and schedule auto-dismiss when jobs complete.
  useEffect(() => {
    if (!jobsData) {
      return;
    }
    const timers: ReturnType<typeof setTimeout>[] = [];
    let anyCompleted = false;

    for (const serverJob of jobsData) {
      const tracked = trackedJobs.get(serverJob.job_id);
      if (!tracked || tracked.firedComplete || tracked.dismissedAt) {
        continue;
      }
      if (serverJob.status !== "completed") {
        continue;
      }

      anyCompleted = true;

      setTrackedJobs((prev) => {
        const next = new Map(prev);
        const entry = next.get(serverJob.job_id);
        if (entry) {
          next.set(serverJob.job_id, { ...entry, firedComplete: true });
        }
        return next;
      });

      if ((serverJob.errors ?? []).length === 0) {
        const id = serverJob.job_id;
        const timer = setTimeout(() => {
          setTrackedJobs((prev) => {
            const next = new Map(prev);
            const entry = next.get(id);
            if (entry) {
              next.set(id, { ...entry, dismissedAt: Date.now() });
            }
            return next;
          });
        }, AUTO_DISMISS_MS);
        timers.push(timer);
      }
    }

    if (anyCompleted) {
      onJobComplete();
    }

    return () => timers.forEach(clearTimeout);
  }, [jobsData, onJobComplete]);

  const visibleJobs = useMemo<BulkJob[]>(() => {
    return [...trackedJobs.values()]
      .filter((t) => !t.dismissedAt)
      .map((tracked) => {
        const server = jobsData?.find((j) => j.job_id === tracked.jobId);
        return {
          jobId: tracked.jobId,
          totalUsers: server?.total_users ?? tracked.initialTotalUsers,
          processedUsers: server?.processed_users ?? 0,
          status: server ? server.status : "pending",
          errors: (server?.errors ?? []).map((e) => ({
            email: e.email,
            tag: e.tag,
            error: e.error,
          })),
          startedAt: tracked.startedAt,
          completedAt: server?.completed_at ? new Date(server.completed_at) : undefined,
          jobType: tracked.jobType,
        };
      });
  }, [trackedJobs, jobsData]);

  const clampedIndex = Math.min(currentIndex, Math.max(0, visibleJobs.length - 1));
  const currentJob = visibleJobs[clampedIndex] ?? null;

  const goNext = useCallback(
    () => setCurrentIndex((i) => Math.min(i + 1, visibleJobs.length - 1)),
    [visibleJobs.length]
  );

  const goPrev = useCallback(() => setCurrentIndex((i) => Math.max(i - 1, 0)), []);

  const addJob = useCallback((id: string, totalUsers: number, jobType: BulkJobType) => {
    setTrackedJobs((prev) => {
      if (prev.has(id)) {
        return prev;
      }
      return new Map([
        ...prev,
        [
          id,
          {
            jobId: id,
            jobType,
            startedAt: new Date(),
            initialTotalUsers: totalUsers,
            firedComplete: false,
          },
        ],
      ]);
    });
  }, []);

  const addImportJob = useCallback(
    (id: string, total: number) => addJob(id, total, "create"),
    [addJob]
  );

  const addEditJob = useCallback(
    (id: string, total: number) => addJob(id, total, "update"),
    [addJob]
  );

  const dismiss = useCallback(() => {
    if (!currentJob) {
      return;
    }
    const id = currentJob.jobId;
    setTrackedJobs((prev) => {
      const next = new Map(prev);
      const entry = next.get(id);
      if (entry) {
        next.set(id, { ...entry, dismissedAt: Date.now() });
      }
      return next;
    });
  }, [currentJob]);

  return {
    currentJob,
    currentIndex: clampedIndex,
    total: visibleJobs.length,
    goNext,
    goPrev,
    addImportJob,
    addEditJob,
    dismiss,
  };
}
